import { Subject, useHealthblocksSWR } from '@healthblocks-io/core'
import { Auth } from '@healthblocks-io/core/auth-context'
import { ServerProvider, useServerContext } from '@healthblocks-io/core/server'
import { useCallback, useRef } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { type TenantSlug, tenantSlugs } from '@/modules/healthblocks/ServerProvider'

import type { SubjectMember } from '@healthblocks-io/core'
import type { AuthState } from '@healthblocks-io/core/auth-context'
import type { ReactElement } from 'react'

/**
 * Stores authentication data for a user and provides useful methods to interact with it.
 */
export function IntramurosAuthenticationProvider() {
  // Server: TenantGuard provides a server context, we override it here
  const { integrationSynopsUrl } = useServerContext()

  // Authentication
  const data = useRef<AuthState | null | undefined>({})
  const setData = useCallback(() => ({}), [])
  const { key } = useParams()
  const next = useCallback(async () => key ?? '', [key])
  const signOut = useCallback(() => {
    alert('Not implemented')
  }, [])

  // Extract tenant from key (prefix)
  // key starts with azmm_ or olvw_
  const extractedTenant = (key || '').split('_')[0] as TenantSlug
  const tenant = tenantSlugs.includes(extractedTenant) ? extractedTenant : 'azmm'

  return (
    <ServerProvider
      api={integrationSynopsUrl + '/api/intramuros'}
      display='Intramuros'
      integrationSynopsUrl={integrationSynopsUrl}
      tenant={tenant}
    >
      <Auth.Provider value={{ data, setData, next, signOut }}>
        <AuthSubjectProvider include={['identifiers']}>
          <Outlet />
        </AuthSubjectProvider>
      </Auth.Provider>
    </ServerProvider>
  )
}

/**
 * The subject context contains the member profile on which actions are being performed.
 * It is automatically determined from the JWT token.
 */
export function AuthSubjectProvider({
  children,
  include,
}: {
  children: ReactElement
  include?: ('identifiers' | 'project' | 'user')[]
}) {
  // Fetch relevant data
  const subject = useHealthblocksSWR<SubjectMember>('/member?include=' + include?.join(','))

  return <Subject.Provider value={{ memberId: subject.data?.id, subject }}>{children}</Subject.Provider>
}
